package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.data.DraftOrder
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.models.DraftSummary

fun sportLabel(sport: Sport): String = sport.name.replace('_', ' ')

fun orderPrefix(order: DraftOrder?): String = when (order) {
  DraftOrder.SNAKE -> "Snake · "
  DraftOrder.LINEAR -> "Linear · "
  DraftOrder.LOTTERY -> "Lottery · "
  DraftOrder.CUSTOM -> "Custom · "
  null -> ""
}

fun activeMain(d: DraftSummary): String {
  val round = d.currentRound ?: 1
  val pick = d.currentOverallPick ?: 1
  return "${orderPrefix(d.draftOrder)}Round $round · Pick $pick of ${d.totalPicks}"
}

private val SHORT_MONTHS =
  arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

/** Format an ISO-8601 instant as a short "Jun 12" label; empty string if unparseable. */
fun formatShortDate(iso: String): String {
  val date = kotlin.js.Date(iso)
  val month = date.getMonth()
  return if (month in SHORT_MONTHS.indices) "${SHORT_MONTHS[month]} ${date.getDate()}" else ""
}

/** Format an ISO-8601 instant as an uppercase "JUL 7, 2026" stamp; empty string if unparseable. */
fun formatStampDate(iso: String): String {
  val date = kotlin.js.Date(iso)
  val month = date.getMonth()
  return if (month in SHORT_MONTHS.indices)
    "${SHORT_MONTHS[month].uppercase()} ${date.getDate()}, ${date.getFullYear()}" else ""
}

/**
 * Format an ISO-8601 instant as an uppercase "JUL 6, 9:14 AM" stamp — the on-the-clock "since"
 * label. Empty string if unparseable.
 */
fun formatStampDateTime(iso: String): String {
  val date = kotlin.js.Date(iso)
  val month = date.getMonth()
  if (month !in SHORT_MONTHS.indices) return ""
  val hours24 = date.getHours()
  val suffix = if (hours24 < 12) "AM" else "PM"
  val hour12 = when {
    hours24 == 0 -> 12
    hours24 > 12 -> hours24 - 12
    else -> hours24
  }
  val minutes = date.getMinutes().toString().padStart(2, '0')
  return "${SHORT_MONTHS[month].uppercase()} ${date.getDate()}, $hour12:$minutes $suffix"
}

/** Whole days between two ISO-8601 instants (rounded), or 0 if either is unparseable. */
fun daysBetween(startIso: String, endIso: String): Int {
  val start = kotlin.js.Date(startIso).getTime()
  val end = kotlin.js.Date(endIso).getTime()
  if (start.isNaN() || end.isNaN()) return 0
  val millis = end - start
  if (millis <= 0.0) return 0
  return kotlin.math.round(millis / (1000.0 * 60 * 60 * 24)).toInt()
}
